import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, CheckCircle2, Copyright } from 'lucide-react';
import { TERMS_VERSION, PART_HEADERS, SECTIONS } from '../constants/termsContent';

const TermsPage = () => {
  const navigate = useNavigate();
  const [accepted, setAccepted] = useState(false);
  const [checkboxes, setCheckboxes] = useState({
    terms: false, liability: false, thermal: false, ip: false, privacy: false,
  });

  useEffect(() => {
    if (localStorage.getItem('obgh_terms_accepted') === TERMS_VERSION) setAccepted(true);
  }, []);

  const allChecked = Object.values(checkboxes).every(Boolean);

  const handleAccept = () => {
    localStorage.setItem('obgh_terms_accepted', TERMS_VERSION);
    setAccepted(true);
    const token = localStorage.getItem('obgh_token');
    if (token) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/accept-terms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ terms_version: TERMS_VERSION }),
      }).catch(() => {});
    }
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-obgh-black text-obgh-text-primary font-body" data-testid="terms-page">
      <TermsHeader onBack={() => navigate(-1)} />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <WarningBanner />
        <CopyrightBanner />

        <div className="max-h-[50vh] overflow-y-auto pr-2 space-y-6 scrollbar-thin scrollbar-thumb-zinc-700 scrollbar-track-transparent" data-testid="terms-content">
          {[0, 1, 2].map(partIdx => (
            <React.Fragment key={partIdx}>
              <PartHeader label={PART_HEADERS[partIdx].label} testId={`section-header-${PART_HEADERS[partIdx].id}`} />
              {SECTIONS.filter(s => s.part === partIdx).map(section => (
                <Section key={section.id} section={section} />
              ))}
            </React.Fragment>
          ))}
          <TermsFooter version={TERMS_VERSION} />
        </div>

        {!accepted && (
          <AcceptanceForm checkboxes={checkboxes} setCheckboxes={setCheckboxes} allChecked={allChecked} onAccept={handleAccept} />
        )}
        {accepted && (
          <div className="mt-6 p-4 bg-green-900/20 border border-green-500/30 rounded-sm flex items-center gap-3" data-testid="terms-accepted-banner">
            <CheckCircle2 size={18} className="text-green-400" />
            <span className="text-sm text-green-300 font-bold">Toutes les conditions acceptees — Version {TERMS_VERSION}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const TermsHeader = ({ onBack }) => (
  <header className="bg-obgh-surface border-b border-white/10 sticky top-0 z-50 backdrop-blur-glass">
    <div className="container mx-auto px-4 py-3 flex items-center gap-4">
      <button onClick={onBack} className="text-zinc-400 hover:text-obgh-orange transition-colors" data-testid="terms-back-btn">
        <ArrowLeft className="w-6 h-6" />
      </button>
      <div className="flex items-center gap-3">
        <Shield className="w-6 h-6 text-obgh-orange" />
        <h1 className="text-lg font-heading font-black text-obgh-orange uppercase tracking-tight">
          Conditions, Confidentialite & Securite
        </h1>
      </div>
    </div>
  </header>
);

const WarningBanner = () => (
  <div className="mb-6 p-4 bg-red-900/30 border-2 border-red-500/60 rounded-sm" data-testid="terms-warning-banner">
    <div className="flex items-start gap-3">
      <Shield className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
      <div>
        <h2 className="text-sm font-heading font-black text-red-400 uppercase tracking-wider mb-1">Avertissement legal important</h2>
        <p className="text-xs text-red-200/80 leading-relaxed">
          L'utilisation de cette application est soumise aux lois et reglements en vigueur au Quebec et au Canada.
          Vous devez lire et accepter l'integralite des conditions d'utilisation, de la politique de confidentialite
          et des clauses de propriete intellectuelle avant d'utiliser OBGH Territoire Pro.
        </p>
      </div>
    </div>
  </div>
);

const CopyrightBanner = () => (
  <div className="mb-6 p-3 bg-obgh-surface border border-obgh-orange/30 rounded-sm flex items-center justify-center gap-3" data-testid="copyright-banner">
    <Copyright size={16} className="text-obgh-orange" />
    <span className="text-xs font-heading font-black text-obgh-orange uppercase tracking-widest">
      OBGH Territoire Pro — Tous droits reserves — 2026
    </span>
    <Copyright size={16} className="text-obgh-orange" />
  </div>
);

const PartHeader = ({ label, testId }) => (
  <div className="py-2 border-b border-obgh-orange/30 mt-4">
    <h2 className="text-base font-heading font-black text-obgh-orange uppercase tracking-widest text-center" data-testid={testId}>
      {label}
    </h2>
  </div>
);

const Section = ({ section }) => {
  const Icon = section.icon;
  return (
    <section
      id={section.id}
      className={`border rounded-sm p-5 ${section.critical ? 'bg-red-900/10 border-red-500/30' : 'bg-obgh-surface border-white/10'}`}
      data-testid={`terms-section-${section.id}`}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className={section.critical ? 'text-red-400' : 'text-obgh-orange'}><Icon className="w-5 h-5" /></div>
        <h3 className={`text-sm font-heading font-bold uppercase tracking-wider ${section.critical ? 'text-red-400' : 'text-white'}`}>{section.title}</h3>
      </div>
      <div className="text-xs text-zinc-300 leading-relaxed space-y-2">
        {section.content.map((block, i) => (
          <ContentBlock key={`${section.id}-${block.type}-${i}`} block={block} />
        ))}
      </div>
    </section>
  );
};

const ContentBlock = ({ block }) => {
  if (block.type === 'p') return <p>{block.text}</p>;
  if (block.type === 'warning') return <p className="text-red-300 font-bold">{block.text}</p>;
  if (block.type === 'ul') return (
    <ul className="list-disc list-inside space-y-1 ml-2">
      {block.items.map((item) => <li key={item.slice(0, 30)}>{item}</li>)}
    </ul>
  );
  if (block.type === 'ul-danger') return (
    <ul className="list-disc list-inside space-y-1 ml-2 text-red-200/80">
      {block.items.map((item) => <li key={item.slice(0, 30)}>{item}</li>)}
    </ul>
  );
  return null;
};

const TermsFooter = ({ version }) => (
  <div className="bg-obgh-surface border border-obgh-orange/30 rounded-sm p-5 text-center space-y-3" data-testid="terms-footer">
    <div className="flex items-center justify-center gap-2">
      <Shield size={16} className="text-obgh-orange" />
      <span className="text-sm font-heading font-black text-obgh-orange uppercase tracking-widest">OBGH Territoire Pro</span>
      <Shield size={16} className="text-obgh-orange" />
    </div>
    <p className="text-xs text-zinc-300 font-bold">TOUS DROITS RESERVES</p>
    <p className="text-[10px] text-zinc-500 leading-relaxed max-w-lg mx-auto">
      L'ensemble du contenu, des technologies, des algorithmes d'IA, du design et de la marque
      OBGH Territoire Pro sont proteges par les lois canadiennes et internationales.
    </p>
    <p className="text-xs text-zinc-400">Pour toute question : <span className="text-obgh-orange font-bold">support@obgh.ca</span></p>
    <p className="text-[10px] text-zinc-600 font-mono uppercase tracking-wider">Quebec, Canada — Version {version}</p>
  </div>
);

const CHECKBOX_LABELS = [
  { key: 'terms', text: "J'ai lu et j'accepte les conditions d'utilisation dans leur integralite." },
  { key: 'liability', text: "Je comprends que OBGH Territoire Pro ne peut etre tenu responsable de mes actions et que j'utilise l'application a mes propres risques." },
  { key: 'thermal', text: "Je m'engage a utiliser la vision thermique et l'IA uniquement a des fins legales et conformement aux lois de chasse du Quebec et du Canada." },
  { key: 'ip', text: "Je reconnais que les outils IA de OBGH sont exclusifs, proteges et non reproductibles. Je m'engage a ne pas copier, extraire ou utiliser les resultats pour entrainer d'autres systemes IA." },
  { key: 'privacy', text: "J'ai lu et j'accepte la politique de confidentialite et les conditions de paiement." },
];

const AcceptanceForm = ({ checkboxes, setCheckboxes, allChecked, onAccept }) => (
  <div className="mt-6 bg-obgh-surface border-2 border-obgh-orange/40 rounded-sm p-5" data-testid="terms-acceptance">
    <div className="space-y-3 mb-5">
      {CHECKBOX_LABELS.map(({ key, text }) => (
        <label key={key} className="flex items-start gap-3 cursor-pointer group">
          <input
            type="checkbox"
            checked={checkboxes[key]}
            onChange={(e) => setCheckboxes(p => ({ ...p, [key]: e.target.checked }))}
            className="mt-0.5 w-4 h-4 accent-obgh-orange rounded-sm"
            data-testid={`checkbox-${key}`}
          />
          <span className="text-xs text-zinc-300 group-hover:text-white transition-colors">{text}</span>
        </label>
      ))}
    </div>
    <button
      onClick={onAccept}
      disabled={!allChecked}
      data-testid="accept-terms-btn"
      className={`w-full py-3 rounded-sm font-heading font-black uppercase tracking-wider text-sm transition-all ${
        allChecked ? 'bg-obgh-orange text-black hover:bg-obgh-orange/90 cursor-pointer' : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
      }`}
    >
      {allChecked ? (
        <span className="flex items-center justify-center gap-2"><CheckCircle2 size={16} /> J'ACCEPTE TOUTES LES CONDITIONS</span>
      ) : 'VEUILLEZ COCHER TOUTES LES CASES (5/5)'}
    </button>
  </div>
);

export default TermsPage;
